/**
 * Ticket Lifecycle v2 — Slice 3
 *
 * Small chip showing the FOH/BOH custody state of a ticket.
 * Defaults to BOH / with_tech when no custody row exists yet (consistent
 * with the lazy-init position in `_apply_custody_transition`).
 *
 * Display-only: the chip never gates scheduling, timer, parking, rollover,
 * or bay assignment. See docs/lifecycle/TICKET_LIFECYCLE_V2_SPEC.md §4.
 */
import { Badge } from '@/components/ui/badge';
import { Wrench, UserRound, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DEFAULT_CUSTODY,
  custodyStatusLabel,
  type TicketCustodyRow,
} from '@/hooks/tickets/useTicketCustody';
import type { CustodyOwner, CustodyStatus } from '@/lib/tickets/lifecycle/types';

interface Props {
  custody: TicketCustodyRow | null;
  /**
   * Slice 4C.1: when provided, the badge becomes clickable in the
   * `pending_tech_accept` state only — clicking it should fire the
   * BOH-accept transition. For any other status the handler is
   * ignored and the badge renders as a static display chip.
   *
   * The kebab "Accept Back" remains the canonical fallback; this is a
   * convenience affordance, not a replacement.
   */
  onAcceptBoh?: () => void;
}

export function TicketCustodyBadge({ custody, onAcceptBoh }: Props) {
  const owner: CustodyOwner = custody?.custody_owner ?? DEFAULT_CUSTODY.custody_owner;
  const status: CustodyStatus =
    custody?.custody_status ?? DEFAULT_CUSTODY.custody_status;
  const isPending =
    status === 'pending_advisor_accept' || status === 'pending_tech_accept';
  // Slice 4C: pending_tech_accept is a BOH-return alert state — the
  // technician needs to know a ticket has been kicked back. Use a
  // destructive variant + AlertCircle icon to make the return loud.
  const isBohReturn = status === 'pending_tech_accept';
  const Icon = isBohReturn ? AlertCircle : owner === 'FOH' ? UserRound : Wrench;
  const variant = isBohReturn ? 'destructive' : isPending ? 'outline' : 'secondary';

  // Slice 4C.1: clickable only when the handler is wired AND the state
  // is the BOH-return alert state. Other states render unchanged.
  const isClickable = isBohReturn && typeof onAcceptBoh === 'function';
  const titleText = isBohReturn
    ? isClickable
      ? `${owner} · ${custodyStatusLabel(status)} — click to accept ticket back to BOH`
      : `${owner} · ${custodyStatusLabel(status)} — needs technician accept`
    : `${owner} · ${custodyStatusLabel(status)}`;

  const labelEl = (
    <Badge
      variant={variant}
      className={cn(
        'text-xs gap-1',
        isBohReturn && 'animate-pulse',
        isClickable && 'cursor-pointer hover:opacity-90',
      )}
      data-testid="ticket-custody-badge"
      data-custody-owner={owner}
      data-custody-status={status}
      title={titleText}
    >
      <Icon className="h-3 w-3" />
      {owner} · {custodyStatusLabel(status)}
    </Badge>
  );

  if (!isClickable) return labelEl;

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onAcceptBoh!();
      }}
      className="inline-flex items-center"
      aria-label="Accept ticket back to BOH"
      data-testid="ticket-custody-badge-accept-back"
    >
      {labelEl}
    </button>
  );
}
